/**
 * The hazard lane under each list group and on the detail screen (target 4a):
 * one segment per slice on a time window, a tick at each midnight in the
 * place's timezone, and the "Now" line. Geometry only, as fractions of the
 * window (0..1); each app draws it. The period rows under the lane are its
 * text twin, so the lane itself is hidden from screen readers in both apps.
 *
 * Imports `./model`, `./time` and `../Dates`, never the utils barrel
 * (`importCycles.test.ts`).
 */
import type {WeatherWarning} from '../../api/types';
import * as Dates from '../Dates';
import {groupByHazard} from './model';
import {laneDayLabel, WarningTimeOptions} from './time';
import type {
  WarningListGroup,
  WarningListSlice,
  WarningListViewModel,
  WarningTone,
} from './view';

/** How far back the window reaches when everything starts later. */
export const LANE_LEAD_SECONDS = 3 * 3600;
/** Past the last end, so a 23:59:59 end does not sit on the edge. */
const LANE_TAIL_SECONDS = 60;
/** How far an open end ("until further notice") is drawn past the last start. */
const OPEN_END_SECONDS = 24 * 3600;
/** A segment carries its severity word only when at least this wide... */
const WORD_MIN_WIDTH = 0.14;
/** ...and only when the lane has at most this many segments. */
const WORD_MAX_SEGMENTS = 4;
/** A midnight tick this close to the right edge would collide with it. */
const TICK_MAX_LEFT = 0.9;
/** Ticks drawn at most: two weeks of a lane is already unreadable. */
const MAX_TICKS = 14;

/** Paint order: the worse level last, so a milder overlapping slice never hides it. */
const TONE_RANK: Record<WarningTone, number> = {
  warningUnknown: 0,
  warningMinor: 1,
  warningModerate: 2,
  warningSevere: 3,
  warningExtreme: 4,
};

export type WarningLaneSlice = Pick<
  WarningListSlice,
  'id' | 'tone' | 'severity' | 'start' | 'end'
>;

export type WarningLaneSegment = {
  id: number;
  tone: WarningTone;
  /** The severity word to print in the segment; null for unknown. */
  severity: string | null;
  /** Fraction of the window from its start. */
  left: number;
  /** Fraction of the window it covers. Segments come mildest first (paint order). */
  width: number;
  /** Whether the segment is wide enough (and the lane sparse enough) for its word. */
  showWord: boolean;
};

export type WarningLaneTick = {
  /** Epoch seconds of the midnight. */
  at: number;
  left: number;
  /** "Today", "Tomorrow", then the weekday. */
  label: string;
};

export type WarningLane = {
  /** The window, epoch seconds. */
  start: number;
  end: number;
  segments: WarningLaneSegment[];
  ticks: WarningLaneTick[];
  /** The Now line, or null when now lies outside the window. */
  now: number | null;
};

export const warningLane = (
  slices: readonly WarningLaneSlice[],
  options: WarningTimeOptions,
): WarningLane => {
  const {now, timezone} = options;
  const start = Math.min(
    now - LANE_LEAD_SECONDS,
    ...slices.map((s) => s.start),
  );
  const closedEnds = slices
    .filter((s) => s.end !== null)
    .map((s) => (s.end as number) + LANE_TAIL_SECONDS);
  const openEnd = slices.some((s) => s.end === null)
    ? Math.max(now, ...slices.map((s) => s.start)) + OPEN_END_SECONDS
    : -Infinity;
  // An open end reaches a day past the last start, and never cuts a later
  // closed end off the window.
  const end = Math.max(openEnd, ...closedEnds);
  const span = end - start;
  const at = (t: number) => (t - start) / span;

  const segments: WarningLaneSegment[] = [];
  // Slices of different levels can overlap (a severe hour inside a moderate
  // day is its own slice): drawn mildest first, the worse one on top.
  const painted = [...slices].sort(
    (a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone],
  );
  for (const slice of painted) {
    const a = Math.max(slice.start, start);
    const b = Math.min(slice.end ?? end, end);
    if (b <= a) {
      continue;
    }
    const width = (b - a) / span;
    segments.push({
      id: slice.id,
      tone: slice.tone,
      severity: slice.severity,
      left: at(a),
      width,
      showWord:
        slice.severity !== null &&
        slices.length <= WORD_MAX_SEGMENTS &&
        width > WORD_MIN_WIDTH,
    });
  }

  const ticks: WarningLaneTick[] = [];
  // Calendar days, not 86,400 s steps: a DST change makes a day 23 or 25 h.
  const midnight = Dates.timestampToMoment(start, timezone).startOf('day');
  for (
    midnight.add(1, 'day');
    midnight.unix() <= end && ticks.length < MAX_TICKS;
    midnight.add(1, 'day')
  ) {
    const t = midnight.unix();
    if (at(t) < TICK_MAX_LEFT) {
      ticks.push({at: t, left: at(t), label: laneDayLabel(t, options)});
    }
  }

  return {
    start,
    end,
    segments,
    ticks,
    now: now >= start && now <= end ? at(now) : null,
  };
};

/** Both apps draw the tick and Now labels at this size (px on the web, dp on mobile). */
export const LANE_LABEL_FONT_SIZE = 10.5;
/** A tick's label starts this far after its tick (target 4a). */
export const LANE_TICK_LABEL_INSET = 3;
/** The least clear space between two labels. */
export const LANE_LABEL_GAP = 4;
/**
 * Estimated advance per character, in ems. Every lane word of the 25 locales
 * ("Now", "Today", "Tomorrow", the seven short weekdays) was measured in
 * Chromium at 10.5 px in Inter (the webapp's font), the macOS system font
 * (SF), Helvetica Neue and Arial: 0.75 is at or above all of them (widest
 * "Ma" in Inter at 0.733 em), and CJK and Hangul run a full em (1.02).
 * `WeatherWarningsLaneLabels.test.ts` pins a sample. Not measured: Android's
 * Roboto and SF as React Native draws it.
 */
const EM_PER_CHAR = 0.75;
const EM_PER_WIDE_CHAR = 1.1;
// Hangul Jamo, CJK (radicals to ideographs), Hangul syllables, CJK
// compatibility ideographs, full-width forms.
const WIDE_CHAR =
  /[\u1100-\u11ff\u2e80-\u9fff\uac00-\ud7af\uf900-\ufaff\uff00-\uffef]/;

/**
 * A conservative width for a lane label: never narrower than the drawn text,
 * so two labels kept apart by it never touch. `fontScale` is the platform's
 * text scaling (RN `fontScale`; 1 on the web, where zoom scales the lane too).
 */
export const estimateLaneLabelWidth = (text: string, fontScale = 1): number =>
  [...text].reduce(
    (sum, char) =>
      sum + (WIDE_CHAR.test(char) ? EM_PER_WIDE_CHAR : EM_PER_CHAR),
    0,
  ) *
  LANE_LABEL_FONT_SIZE *
  fontScale;

export type WarningLaneLabel = {
  text: string;
  /** Left edge from the lane's start, px (dp). */
  x: number;
  /** The estimated width the box reserves. */
  width: number;
};

export type WarningLaneNowLabel = WarningLaneLabel & {
  /**
   * Where the text sits in its box, which is wider than the drawn text:
   * centred on the line, or ending / starting at it when moved aside.
   */
  align: 'center' | 'end' | 'start';
};

export type WarningLaneLabels = {
  now: WarningLaneNowLabel | null;
  /** Only the tick labels to draw: one too close to "Now" is left out. */
  ticks: (WarningLaneLabel & {at: number})[];
};

const clash = (a: WarningLaneLabel, b: WarningLaneLabel) =>
  a.x < b.x + b.width + LANE_LABEL_GAP && b.x < a.x + a.width + LANE_LABEL_GAP;

/**
 * Where the lane's labels sit, in px (dp) of a lane `width` wide, so "Now"
 * and a midnight tick's day never print over each other ("Nowday").
 *
 * A tick's label starts just after its tick, and never before it: it names the
 * day that starts there. "Now" is centred on its line, kept inside the lane.
 * When that box meets a tick label, "Now" moves to one side of its line,
 * ending at it or starting at it, whichever clears every tick label. When
 * neither does, "Now" stays centred and each clashing tick label is:
 *
 * - left out when its tick is at or before the line (the line a few minutes
 *   past "Today"): its dashed line stays, and "Now" already says which day
 *   it is;
 * - moved right, past "Now", when its tick is after the line (Now clamped at
 *   the lane's start, an upcoming warning's "Tomorrow" just ahead), unless
 *   that runs it into the next day's label or off the lane; then left out.
 *
 * Nothing is placed until the lane has a width (0 before the first layout).
 */
export const warningLaneLabels = (
  lane: Pick<WarningLane, 'now' | 'ticks'>,
  {
    width,
    nowLabel,
    fontScale = 1,
  }: {width: number; nowLabel: string; fontScale?: number},
): WarningLaneLabels => {
  if (width <= 0) {
    return {now: null, ticks: []};
  }
  const ticks = lane.ticks.map(({at, left, label}) => ({
    at,
    text: label,
    x: left * width + LANE_TICK_LABEL_INSET,
    width: estimateLaneLabelWidth(label, fontScale),
  }));
  if (lane.now === null) {
    return {now: null, ticks};
  }

  const line = lane.now * width;
  const nowWidth = estimateLaneLabelWidth(nowLabel, fontScale);
  const inside = (x: number) =>
    Math.min(Math.max(x, 0), Math.max(width - nowWidth, 0));
  const place = (
    x: number,
    align: WarningLaneNowLabel['align'],
  ): WarningLaneNowLabel => ({
    text: nowLabel,
    x: inside(x),
    width: nowWidth,
    align,
  });
  const centred = place(line - nowWidth / 2, 'center');
  const now =
    [centred, place(line - nowWidth, 'end'), place(line, 'start')].find((box) =>
      ticks.every((tick) => !clash(box, tick)),
    ) ?? centred;
  const kept: WarningLaneLabels['ticks'] = [];
  ticks.forEach((tick, i) => {
    if (!clash(now, tick)) {
      kept.push(tick);
      return;
    }
    if (lane.ticks[i].left <= lane.now!) {
      return;
    }
    const moved = {...tick, x: now.x + now.width + LANE_LABEL_GAP};
    const next = ticks[i + 1];
    if (moved.x + moved.width <= width && !(next && clash(moved, next))) {
      kept.push(moved);
    }
  });
  return {now, ticks: kept};
};

/**
 * The list group (lane, period rows, window) a warning row belongs to, as
 * the list reads it now: the detail screen draws the same lane. Found through
 * the rows merged into each spell, so a row folded into an earlier one's
 * slice is still found. Null once none of its hazard's rows is current.
 */
export const listGroupFor = (
  list: WarningListViewModel,
  warning: WeatherWarning,
  rows: readonly WeatherWarning[],
  now: number,
): WarningListGroup | null => {
  const all = [warning, ...rows.filter((r) => r.id !== warning.id)];
  // Its spell over every row, expired ones included (as `detailViewModel`
  // reads it): AEMET's today row has expired at 00:30 while the episode it
  // opened goes on in tomorrow's row.
  const spell = groupByHazard(all, Number.NEGATIVE_INFINITY)
    .flatMap((g) => g.slices)
    .find((s) => s.warnings.some((w) => w.id === warning.id));
  const ids = new Set((spell?.warnings ?? [warning]).map((w) => w.id));
  const group = groupByHazard(all, now).find((g) =>
    g.slices.some((s) => s.warnings.some((w) => ids.has(w.id))),
  );
  if (!group) {
    return null;
  }
  return (
    list.sections
      .flatMap((section) => section.groups)
      .find((g) => g.key === group.key) ?? null
  );
};

/** The window a group's lane covers: first start to last end (null: open). */
export const warningGroupWindow = (
  group: Pick<WarningListGroup, 'slices'>,
): {start: number; end: number | null} => ({
  start: Math.min(...group.slices.map((s) => s.start)),
  end: group.slices.some((s) => s.end === null)
    ? null
    : Math.max(...group.slices.map((s) => s.end as number)),
});

/**
 * What the warnings list / page shows, before its own empty state:
 *
 * - `error`: the last fetch failed and nothing fresh is stored. An outage
 *   must never read as "no warnings".
 * - `pending`: no response yet and nothing failed: show nothing (no
 *   loading placeholder, target 9a).
 * - `ready`: the stored response, current or a little stale (a refresh is
 *   under way, or a failed refresh over data still under the hour).
 */
export const warningListState = ({
  hasError,
  isStale,
  hasResponse,
}: {
  hasError: boolean;
  isStale: boolean;
  hasResponse: boolean;
}): 'error' | 'pending' | 'ready' => {
  if (hasError && isStale) {
    return 'error';
  }
  return hasResponse ? 'ready' : 'pending';
};
